import React, { Suspense, useEffect, useRef, useState } from "react";
import "./about.css";
import aboutImg from "../../images/about.webp";
import { useProfile } from "../../hooks/useProfile";

const Astronaut3D = React.lazy(() => import("../../components/Astronaut3D"));

export default function About() {
  const [show3D, setShow3D] = useState(false);
  const [prefer3D, setPrefer3D] = useState(true);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [fallback3D, setFallback3D] = useState(false);
  const profile = useProfile();
  const mountRef = useRef(null);
  const timerRef = useRef(null);

  // Detecta conexão/CPU/memória para decidir se deve tentar 3D
  useEffect(() => {
    try {
      const conn =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;
      const saveData = conn?.saveData === true;
      const effType = (conn?.effectiveType || "").toLowerCase();
      const slowType = effType.includes("2g") || effType.includes("slow-2g");
      const downlink =
        typeof conn?.downlink === "number" ? conn.downlink : undefined;
      const slowDownlink =
        typeof downlink === "number" && downlink > 0 && downlink < 1.5; // Mbps
      const lowCPU =
        typeof navigator.hardwareConcurrency === "number" &&
        navigator.hardwareConcurrency <= 2;
      const lowMem =
        typeof navigator.deviceMemory === "number" &&
        navigator.deviceMemory < 4;
      const shouldAvoid3D =
        saveData || slowType || slowDownlink || lowCPU || lowMem;
      setPrefer3D(!shouldAvoid3D);
    } catch (_) {
      // Fica true por padrão em navegadores sem API
    }
  }, []);

  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShow3D(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px 0px", threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const readyToAttempt3D = show3D && prefer3D;

  // Timeout de carregamento: se 3D não carregar em 3.5s, exibe imagem
  useEffect(() => {
    if (!readyToAttempt3D) return;
    timerRef.current && clearTimeout(timerRef.current);
    setFallback3D(false);
    timerRef.current = setTimeout(() => {
      if (!modelLoaded) setFallback3D(true);
    }, 1500);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [readyToAttempt3D, modelLoaded]);

  const about = profile.about || {};
  const focusAreas = about.focusAreas || [];
  const highlights = about.highlights || [];
  const tags = about.tags || [];
  const terminal = about.terminal || {};
  const badge = about.badge || {};
  const imageAlt = about.imageAlt || "";

  return (
    <section
      id="about"
      className="s-about target-section" // <— só s-about aqui
      data-pin
      data-pin-end="+=50%"
      data-aos="fade-up"
    >
      <div className="about-grid">
        {" "}
        {/* <— grid interno full-responsivo */}
        <div
          className="image-about"
          data-reveal-left
          data-aos="fade-right"
          data-aos-delay="50"
          ref={mountRef}
        >
          {readyToAttempt3D && !fallback3D ? (
            <Suspense
              fallback={
                <img
                  src={aboutImg}
                  alt={imageAlt}
                  style={{
                    width: "100%",
                    height: 520,
                    objectFit: "cover",
                    borderRadius: 16,
                  }}
                  loading="lazy"
                  decoding="async"
                />
              }
            >
              <Astronaut3D onLoaded={() => setModelLoaded(true)} />
            </Suspense>
          ) : show3D ? (
            <img
              src={aboutImg}
              alt={imageAlt}
              style={{
                width: "100%",
                height: 520,
                objectFit: "cover",
                borderRadius: 16,
              }}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div
              style={{ height: 520, borderRadius: 16, background: "#0b132b" }}
            />
          )}
        </div>
        {/* Terminal estilo Tailwind */}
        <div
          className="w-full max-w-[720px] mb-12 lg:mb-0"
          data-reveal-right
          data-aos="fade-left"
          data-aos-delay="150"
        >
          <div className="terminal rounded-lg overflow-hidden shadow-xl">
            <div className="terminal-header flex items-center justify-between px-5 py-3">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-red-500/90 shadow-inner shadow-red-500/40"></span>
                <span className="h-3 w-3 rounded-full bg-yellow-400/90 shadow-inner shadow-yellow-500/40"></span>
                <span className="h-3 w-3 rounded-full bg-emerald-400/90 shadow-inner shadow-emerald-500/40"></span>
              </div>
              <span className="hidden text-xs font-semibold uppercase tracking-[0.32em] text-slate-200/60 sm:inline-flex">
                {about.md}
              </span>
            </div>
            <div
              className="terminal-body space-y-6 p-5 text-slate-100 md:p-6"
              id="terminal"
            >
              <div className="flex flex-wrap items-center gap-3 font-mono text-sm md:text-base">
                <span className="text-emerald-400">{terminal.user}</span>
                <span className="text-slate-500">~/{about.type}</span>
                <span className="text-emerald-400">$</span>
                <span className="text-slate-200">{terminal.command}</span>
              </div>
              <p className="font-sans text-base leading-relaxed text-slate-100/95 md:text-lg">
                {about.description}
              </p>
              <div className="grid gap-4 rounded-xl border border-slate-700/40 bg-slate-900/40 p-4 sm:grid-cols-3">
                {focusAreas.map((item) => (
                  <div key={item.label} className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400/80">
                      {item.label}
                    </span>
                    <p className="text-sm leading-relaxed text-slate-100/90">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
              <div className="space-y-4">
                {badge.label && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-3 py-1 text-sm font-semibold text-emerald-300">
                    {badge.icon && (
                      <span className="text-emerald-300">{badge.icon}</span>
                    )}
                    {badge.label}
                  </span>
                )}
                <ul className="space-y-2 text-sm leading-relaxed text-slate-100/90 md:text-base">
                  {highlights.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="text-emerald-400">*</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-wrap gap-4 border-t border-slate-700/50 pt-4 text-sm text-slate-300/90">
                {tags.map((tag) => (
                  <span key={tag} className="flex items-center gap-2">
                    <span className="text-emerald-400">#</span>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
